import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIME_STRING_RE = /^\d{1,2}:\d{2}$/;

/** Parse react-hook-form time values into a `Date` (time-of-day on today's date). */
export function parseTimeFormValue(value: unknown, fallback?: string): Date | null {
  if (value == null || value === '') {
    if (fallback && TIME_STRING_RE.test(fallback)) {
      const [hours, minutes] = fallback.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      return date;
    }
    return null;
  }

  if (typeof value === 'string' && TIME_STRING_RE.test(value)) {
    const [hours, minutes] = value.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.toDate() : null;
  }

  try {
    const parsed = dayjs.tz(value as string | Date, 'Asia/Jerusalem');
    return parsed.isValid() ? parsed.toDate() : null;
  } catch {
    return null;
  }
}

export function formatTimeString(date: Date | null): string | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Normalize form time values (`HH:mm`, Dayjs, Date) for API submission. */
export function formatTimeForSubmission(value: unknown): string {
  if (value == null || value === '') return '';

  if (typeof value === 'string' && TIME_STRING_RE.test(value)) {
    return value;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format('HH:mm') : '';
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatTimeString(value) ?? '';
  }

  const parsed = dayjs(value as string | Date);
  return parsed.isValid() ? parsed.format('HH:mm') : '';
}
